import {
  AccountTree as AccountTreeIcon,
  ChevronRight as ChevronRightIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
} from "@mui/icons-material";
import DoneIcon from "@mui/icons-material/Done";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import type { TestContext, TestRunStep } from "../../../types";
import { formatDuration, getRunStatusColor } from "../../../utils";
import type { StepGroupMetadata } from "../../../utils/group-utils";
import { clickableProps } from "../../../utils/keyboard";
import { humanizeText } from "../../../utils/text";

interface TestStepCardProps {
  ctx: TestContext;
  isSelected: boolean;
  isInSelectionMode: boolean;
  isChecked?: boolean;
  stepNumber: number;
  stepInfo: TestRunStep | null;
  isDragging?: boolean;
  isDragOver?: boolean;
  isDragActive?: boolean;
  groupMetadata?: StepGroupMetadata;
  isGroupCollapsed?: boolean;
  onSelect: (ctx: TestContext) => void;
  onToggleSelection: (ctx: TestContext, checked: boolean) => void;
  onDelete: (ctx: TestContext) => void;
  onDuplicate: (ctx: TestContext) => void;
  onToggleDisable: (ctx: TestContext) => void;
  onToggleGroupCollapse?: (groupName: string) => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

export function TestStepCard({
  ctx,
  isSelected,
  isInSelectionMode,
  isChecked: isCheckedProp,
  stepNumber,
  stepInfo,
  isDragging = false,
  isDragOver = false,
  isDragActive = false,
  groupMetadata,
  isGroupCollapsed = false,
  onSelect,
  onToggleSelection,
  onDelete,
  onDuplicate,
  onToggleDisable,
  onToggleGroupCollapse,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: TestStepCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const isChecked = isCheckedProp ?? false;

  // Clear hover state when drag becomes active (if this is not the dragged/dragOver item)
  useEffect(() => {
    if (isDragActive && !isDragging && !isDragOver) {
      setIsHovered(false);
    }
  }, [isDragActive, isDragging, isDragOver]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onToggleSelection(ctx, e.target.checked);
  };

  const handleCardClick = () => {
    // Prevent click if we just finished dragging
    if (hasDragged) {
      setHasDragged(false);
      return;
    }
    onSelect(ctx);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(ctx);
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDuplicate(ctx);
  };

  const handleToggleDisable = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleDisable(ctx);
  };

  const handleDragStartInternal = (e: React.DragEvent) => {
    setHasDragged(true);
    setIsHovered(false); // Clear hover state when starting drag
    onDragStart();
    // Set drag image to be the card itself
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEndInternal = () => {
    // Small delay to prevent click after drag
    setTimeout(() => {
      setHasDragged(false);
    }, 100);
    onDragEnd();
  };

  // Determine if hover should be disabled
  const disableHover = isDragActive && !isDragging && !isDragOver;

  const handleToggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (groupMetadata?.groupName && onToggleGroupCollapse) {
      onToggleGroupCollapse(groupMetadata.groupName);
    }
  };

  // Check if this is a grouped step (not a group check)
  const isGroupedStep =
    groupMetadata?.belongsToGroup && !groupMetadata.isGroupCheck && groupMetadata.indentLevel > 0;

  return (
    <Box
      sx={{
        paddingLeft: isGroupedStep ? "40px" : 0,
        position: "relative",
      }}
    >
      {/* Left border line for grouped steps (not group checks) */}
      {isGroupedStep && (
        <Box
          sx={{
            position: "absolute",
            left: 8,
            top: 0,
            bottom: 0,
            width: "3px",
            backgroundColor: "primary.main",
            opacity: isSelected ? 0.5 : 0.3,
            borderRadius: "2px",
            transition: "opacity 0.15s ease-in-out",
          }}
        />
      )}

      <Box
        draggable
        onMouseEnter={() => {
          if (!disableHover) {
            setIsHovered(true);
          }
        }}
        onMouseLeave={() => {
          if (!disableHover) {
            setIsHovered(false);
          }
        }}
        {...clickableProps(handleCardClick)}
        onClick={handleCardClick}
        onDragStart={handleDragStartInternal}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onDragEnd={handleDragEndInternal}
        sx={{
          position: "relative",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: isDragOver
            ? "primary.main"
            : isSelected
              ? isGroupedStep
                ? "primary.main"
                : "primary.main"
              : isGroupedStep
                ? "rgba(22, 0, 234, 0.15)"
                : "rgba(0, 0, 0, 0.12)",
          backgroundColor: isDragOver
            ? "rgba(22, 0, 234, 0.08)"
            : isSelected
              ? isGroupedStep
                ? "rgba(22, 0, 234, 0.06)"
                : "rgba(22, 0, 234, 0.04)"
              : isGroupedStep
                ? "rgba(22, 0, 234, 0.02)"
                : ctx.disabled
                  ? "rgba(0, 0, 0, 0.02)"
                  : "background.paper",
          padding: "10px 12px",
          cursor: isDragging ? "grabbing" : "grab",
          opacity: isDragging ? 0.5 : 1,
          // Disable transitions during drag to prevent flickering
          transition: disableHover ? "none" : "all 0.15s ease-in-out",
          transform: isDragOver ? "translateY(4px)" : "none",
          // Completely prevent hover styles when drag is active (but keep dragOver working)
          ...(disableHover
            ? {
                // Force disable hover by setting styles that override :hover
                "&:hover": {
                  borderColor: isSelected
                    ? "primary.main"
                    : isGroupedStep
                      ? "rgba(22, 0, 234, 0.15)"
                      : "rgba(0, 0, 0, 0.12)",
                  backgroundColor: isSelected
                    ? isGroupedStep
                      ? "rgba(22, 0, 234, 0.06)"
                      : "rgba(22, 0, 234, 0.04)"
                    : isGroupedStep
                      ? "rgba(22, 0, 234, 0.02)"
                      : ctx.disabled
                        ? "rgba(0, 0, 0, 0.02)"
                        : "background.paper",
                },
              }
            : {
                "&:hover": {
                  borderColor: isDragOver
                    ? "primary.main"
                    : isSelected
                      ? "primary.main"
                      : isGroupedStep
                        ? "rgba(22, 0, 234, 0.3)"
                        : "rgba(0, 0, 0, 0.2)",
                  backgroundColor: isDragOver
                    ? "rgba(22, 0, 234, 0.1)"
                    : isSelected
                      ? isGroupedStep
                        ? "rgba(22, 0, 234, 0.08)"
                        : "rgba(22, 0, 234, 0.06)"
                      : isGroupedStep
                        ? "rgba(22, 0, 234, 0.04)"
                        : "rgba(0, 0, 0, 0.02)",
                },
              }),
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Collapse/expand button for group checks */}
          {groupMetadata?.isGroupCheck && (
            <IconButton
              size="small"
              onClick={handleToggleCollapse}
              aria-label={isGroupCollapsed ? "Expand" : "Collapse"}
              sx={{
                padding: "2px",
                color: "text.secondary",
                "&:hover": {
                  color: "primary.main",
                  backgroundColor: "rgba(22, 0, 234, 0.08)",
                },
              }}
            >
              {isGroupCollapsed ? (
                <ChevronRightIcon sx={{ fontSize: "18px" }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: "18px" }} />
              )}
            </IconButton>
          )}

          {/* Checkbox - visible on hover or when in selection mode */}
          <Box
            sx={{
              opacity: (!isDragActive && isHovered) || isInSelectionMode || isChecked ? 1 : 0,
              transition: "opacity 0.15s ease-in-out",
              width:
                (!isDragActive && isHovered) || isInSelectionMode || isChecked ? "24px" : "0px",
              overflow: "hidden",
            }}
          >
            <Checkbox
              size="small"
              checked={isChecked}
              onChange={handleCheckboxChange}
              sx={{
                padding: 0,
                "& .MuiSvgIcon-root": {
                  fontSize: "18px",
                },
              }}
            />
          </Box>

          {/* Step content */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            {/* Group check icon */}
            {groupMetadata?.isGroupCheck && (
              <AccountTreeIcon
                sx={{
                  fontSize: 16,
                  color: "primary.main",
                  flexShrink: 0,
                }}
              />
            )}

            {/* Step number */}
            <Typography
              variant="caption"
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: "text.secondary",
                flexShrink: 0,
              }}
            >
              #{stepNumber}
            </Typography>

            {/* Step name */}
            <Typography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: isSelected ? 600 : 500,
                color: ctx.disabled ? "text.secondary" : "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textDecoration: ctx.disabled ? "line-through" : "none",
              }}
            >
              {ctx.name}
            </Typography>

            {/* Group name chip */}
            {groupMetadata?.groupName && (
              <Chip
                label={groupMetadata.groupName}
                size="small"
                variant={groupMetadata.isGroupCheck ? "filled" : "outlined"}
                color="primary"
                sx={{
                  fontSize: "10px",
                  height: "18px",
                  flexShrink: 0,
                }}
              />
            )}

            {/* Status chip */}
            {stepInfo && !isHovered && (
              <Tooltip title={humanizeText(stepInfo.run_status)}>
                {["failed", "failed_pending", "error"].includes(stepInfo.run_status) ? (
                  <ErrorIcon
                    sx={{
                      fontSize: 12,
                      color: getRunStatusColor(stepInfo.run_status),
                    }}
                  />
                ) : stepInfo.run_status === "passed" ? (
                  <DoneIcon
                    sx={{
                      fontSize: 12,
                      color: getRunStatusColor(stepInfo.run_status),
                    }}
                  />
                ) : ["running", "pending"].includes(stepInfo.run_status) ? (
                  <CircularProgress
                    sx={{ color: getRunStatusColor(stepInfo.run_status) }}
                    size={12}
                  />
                ) : (
                  <span />
                )}
              </Tooltip>
            )}

            {/* Duration */}
            {stepInfo && !isHovered && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "10px",
                  color: "text.secondary",
                  flexShrink: 0,
                }}
              >
                {formatDuration(stepInfo.elapsed_seconds, { decimals: 3 })}
              </Typography>
            )}
          </Stack>

          {/* Action buttons - visible on hover */}
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              opacity: !isDragActive && isHovered ? 1 : 0,
              transition: "opacity 0.15s ease-in-out",
              width: !isDragActive && isHovered ? "auto" : "0px",
              overflow: "hidden",
            }}
          >
            <Tooltip title={ctx.disabled ? "Enable" : "Disable"}>
              <IconButton
                size="small"
                onClick={handleToggleDisable}
                aria-label={ctx.disabled ? "Enable" : "Disable"}
                sx={{
                  padding: "4px",
                  color: "text.secondary",
                  "&:hover": {
                    color: "primary.main",
                    backgroundColor: "rgba(22, 0, 234, 0.08)",
                  },
                }}
              >
                {ctx.disabled ? (
                  <ToggleOffIcon sx={{ fontSize: "16px" }} />
                ) : (
                  <ToggleOnIcon sx={{ fontSize: "16px" }} />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Duplicate">
              <IconButton
                size="small"
                onClick={handleDuplicate}
                aria-label="Duplicate"
                sx={{
                  padding: "4px",
                  color: "text.secondary",
                  "&:hover": {
                    color: "primary.main",
                    backgroundColor: "rgba(22, 0, 234, 0.08)",
                  },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={handleDelete}
                aria-label="Delete"
                sx={{
                  padding: "4px",
                  color: "text.secondary",
                  "&:hover": {
                    color: "error.main",
                    backgroundColor: "rgba(220, 53, 69, 0.08)",
                  },
                }}
              >
                <DeleteIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
